import React, { useState, useEffect } from 'react';
import { MacOSWindow } from './components/MacOSWindow';
import { Dock } from './components/Dock';
import { Section } from './components/Section';
import { Button } from './components/Button';
import { PROJECTS, PARTNERS, ARTICLES } from './constants';
import { Leaf, ArrowRight, CheckCircle2, Globe, Linkedin, ArrowUpRight, Award, Users } from 'lucide-react';

export default function App() {
  const [activeSection, setActiveSection] = useState('hero');

  const scrollToSection = (id: string) => {
    const container = document.getElementById('main-scroll-container');
    const element = document.getElementById(id);
    if (container && element) {
      // Calculate position relative to container
      const topPos = element.offsetTop;
      container.scrollTo({
        top: topPos,
        behavior: 'smooth'
      });
      setActiveSection(id);
    }
  };

  // Intersection Observer to update active section on scroll
  useEffect(() => {
    const container = document.getElementById('main-scroll-container');
    if (!container) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      {
        root: container,
        threshold: 0.3, // Trigger when 30% of section is visible
      }
    );

    const sections = document.querySelectorAll('section');
    sections.forEach((section) => observer.observe(section));

    return () => {
      sections.forEach((section) => observer.unobserve(section));
    };
  }, []);

  return (
    <div className="relative w-full min-h-screen bg-[url('https://images.unsplash.com/photo-1470770841072-f978cf4d019e?q=80&w=2070&auto=format&fit=crop')] bg-cover bg-center overflow-hidden flex items-center justify-center font-roboto">
      <div className="absolute inset-0 bg-black/10 backdrop-blur-[2px]" />
      
      <MacOSWindow title="Salt Palm Development">
        
        {/* HERO */}
        <section id="hero" className="relative w-full min-h-[80vh] flex items-center justify-center p-8 text-center text-white">
          <div className="absolute inset-0 z-0">
             <img 
               src="https://images.unsplash.com/photo-1518780664697-55e3ad937233?q=80&w=2065&auto=format&fit=crop" 
               alt="Modern Architecture" 
               className="w-full h-full object-cover"
             />
             <div className="absolute inset-0 bg-salt-charcoal/40 mix-blend-multiply" />
          </div>
          
          <div className="relative z-10 max-w-4xl mx-auto flex flex-col items-center">
            <div className="mb-8 p-4 bg-white/20 backdrop-blur-md rounded-full border border-white/30 animate-fade-in-up">
              <Leaf size={48} className="text-white" />
            </div>
            <h1 className="font-oswald text-5xl md:text-7xl font-bold uppercase tracking-tight mb-6 drop-shadow-lg animate-fade-in-up delay-100">
              Building Real Estate <br/> That Gives Back
            </h1>
            <p className="font-roboto text-lg md:text-xl font-light max-w-2xl mb-10 text-gray-100 drop-shadow-md animate-fade-in-up delay-200">
              Salt Palm Development is the Southeast’s first carbon-neutral, certified B Corp real estate developer. We create high-quality projects rooted in sustainability, community impact, and long-term value.
            </p>
            <div className="animate-fade-in-up delay-300">
              <Button 
                variant="solid" 
                className="!bg-white !text-salt-teal !border-white hover:!bg-salt-teal hover:!text-white hover:!border-salt-teal"
                onClick={() => scrollToSection('impact')}
              >
                Explore Our Impact
              </Button>
            </div>
          </div>
        </section>

        {/* OUR STORY */}
        <Section id="story" title="Our Story" subtitle="About Us">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="prose prose-lg text-salt-gray">
              <p className="mb-6">
                Salt Palm Development was founded on the belief that real estate can—and should—be a force for good.
              </p>
              <p className="mb-6">
                Based in Florida, Salt Palm became the first real estate developer in the Southeast to achieve B Corp certification and operate as a carbon-neutral business. From the beginning, our approach has been defined by thoughtful design, responsible construction practices, and a commitment to creating value beyond financial returns.
              </p>
              <p>
                Over the years, Salt Palm has completed award-winning projects and reinvested the majority of its profits into initiatives that support people, strengthen communities, and protect the planet.
              </p>
            </div>
            <div className="relative h-[400px] w-full bg-gray-100 rounded-2xl overflow-hidden shadow-2xl transform md:rotate-2 hover:rotate-0 transition-all duration-500">
               <img 
                 src="https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&q=80&w=1000" 
                 alt="Our Office" 
                 className="w-full h-full object-cover"
               />
               <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-6 text-white">
                  <p className="font-oswald uppercase tracking-widest text-sm">Est. Florida</p>
               </div>
            </div>
          </div>
          
          {/* Differentiators */}
          <div className="grid md:grid-cols-3 gap-8 mt-20">
             {[
               { title: "Quality Construction", desc: "Prioritizing durable materials and thoughtful design that balances aesthetics, performance, and long-term impact." },
               { title: "Market Leadership", desc: "Consistently leading by example to redefine responsible real estate development in Florida and beyond." },
               { title: "Purpose-Driven", desc: "As a certified B Corp, we operate with a commitment to transparency, accountability, and positive social impact." }
             ].map((item, idx) => (
               <div key={idx} className="bg-white/50 backdrop-blur-sm p-8 rounded-xl border border-white/60 shadow-lg hover:shadow-xl transition-all duration-300">
                 <div className="w-12 h-12 bg-salt-teal/10 rounded-full flex items-center justify-center mb-6 text-salt-teal">
                    <CheckCircle2 size={24} />
                 </div>
                 <h3 className="font-oswald text-xl font-medium text-salt-charcoal mb-4 uppercase">{item.title}</h3>
                 <p className="font-roboto text-salt-gray leading-relaxed">{item.desc}</p>
               </div>
             ))}
          </div>
        </Section>

        {/* IMPACT */}
        <Section id="impact" className="bg-gradient-to-br from-[#f8fcfc] to-white" title="Business For Good" subtitle="Impact">
          <div className="flex flex-col md:flex-row gap-12 items-start">
            <div className="flex-1 space-y-6 text-salt-gray text-lg">
              <p>
                Salt Palm exists as part of a broader movement to use business as a force for good. As a certified B Corporation, we meet rigorous standards for social and environmental performance, transparency, and accountability.
              </p>
              <p>
                Our carbon-neutral operations, commitment to responsible development, and reinvestment of profits reflect our belief that success should benefit more than just the bottom line.
              </p>
              
              <div className="pt-6 flex flex-wrap gap-4">
                <Button onClick={() => window.open('https://www.bcorporation.net', '_blank')}>Learn About B Corps</Button>
                <Button onClick={() => window.open('#', '_blank')}>View Impact Report</Button>
              </div>
            </div>
            
            <div className="flex-1 flex flex-col items-center justify-center gap-6 p-10 bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-gray-100">
               <div className="w-full flex justify-center gap-8 items-center opacity-80 grayscale hover:grayscale-0 transition-all duration-500">
                  {/* Placeholder Logos for BCorp */}
                  <div className="w-24 h-24 bg-black rounded-full flex items-center justify-center text-white font-bold text-xs text-center p-2">
                    Certified <br/> B Corp
                  </div>
                  <div className="w-24 h-24 border-4 border-black rounded-full flex items-center justify-center font-bold text-xs text-center p-2">
                    Best For <br/> The World <br/> 2021
                  </div>
               </div>
               <p className="text-center text-sm font-medium text-salt-gray/60 uppercase tracking-widest mt-4">Certified Excellence</p>
            </div>
          </div>
        </Section>

        {/* PROJECTS */}
        <Section id="projects" title="Past Projects" subtitle="Portfolio">
           <div className="grid md:grid-cols-2 gap-8">
             {PROJECTS.map((project, idx) => (
               <div key={idx} className="group relative overflow-hidden rounded-2xl shadow-lg cursor-pointer aspect-[4/3]">
                 <img 
                   src={project.image} 
                   alt={project.title} 
                   className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" 
                 />
                 <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-8">
                   <h3 className="text-white font-oswald text-2xl uppercase tracking-wide translate-y-4 group-hover:translate-y-0 transition-transform duration-300">{project.title}</h3>
                   <p className="text-salt-teal font-roboto text-sm uppercase tracking-wider translate-y-4 group-hover:translate-y-0 transition-transform duration-300 delay-75">{project.location}</p>
                 </div>
               </div>
             ))}
           </div>
        </Section>

        {/* RECOGNITION & PARTNERS */}
        <Section id="partners" className="bg-salt-slate/5" title="Recognition & Partners" subtitle="Community">
          <div className="grid md:grid-cols-2 gap-16">
            
            {/* Press */}
            <div>
              <h3 className="font-oswald text-2xl text-salt-charcoal mb-6 flex items-center gap-3">
                <Award className="text-salt-teal" /> In The Press
              </h3>
              <div className="space-y-4">
                {ARTICLES.map((article, idx) => (
                  <a 
                    key={idx} 
                    href={article.link}
                    className="block p-4 bg-white rounded-lg border border-gray-100 hover:border-salt-teal/50 hover:shadow-md transition-all group"
                  >
                    <div className="flex justify-between items-center">
                      <div>
                        <h4 className="font-bold text-salt-charcoal group-hover:text-salt-teal transition-colors">{article.title}</h4>
                        <p className="text-sm text-gray-500">{article.source}</p>
                      </div>
                      <ArrowUpRight size={16} className="text-gray-300 group-hover:text-salt-teal" />
                    </div>
                  </a>
                ))}
              </div>
            </div>

            {/* Partners */}
            <div>
              <h3 className="font-oswald text-2xl text-salt-charcoal mb-6 flex items-center gap-3">
                <Users className="text-salt-teal" /> Our Partners
              </h3>
              <div className="flex flex-wrap gap-3">
                {PARTNERS.map((partner, idx) => (
                  <span 
                    key={idx} 
                    className="px-4 py-2 bg-white rounded-full text-sm font-medium text-gray-600 border border-gray-200 shadow-sm"
                  >
                    {partner.name}
                  </span>
                ))}
              </div>
              <p className="mt-6 text-sm text-gray-500 leading-relaxed">
                We are proud to collaborate with organizations that share our values and commitment to responsible business practices.
              </p>
            </div>
          </div>
        </Section>

        {/* TODAY / FOOTER CTA */}
        <section id="footer" className="bg-salt-slate text-white py-20 px-6 md:px-12 relative overflow-hidden">
           <div className="absolute inset-0 opacity-10 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')]"></div>
           
           <div className="relative max-w-4xl mx-auto text-center">
             <div className="mb-12">
               <h2 className="font-oswald text-4xl mb-4">Today & Looking Ahead</h2>
               <p className="text-gray-300 max-w-2xl mx-auto leading-relaxed">
                 Salt Palm currently holds a parcel in St. Petersburg, Florida. Our focus remains on stewardship, long-term vision, and supporting the broader ecosystem of organizations working to redefine the role of business in society.
               </p>
             </div>

             <div className="h-px w-full bg-white/10 my-12" />

             <h3 className="font-oswald text-3xl mb-8 uppercase tracking-wider">Join The Movement</h3>
             
             <div className="flex flex-col md:flex-row justify-center gap-6 mb-16">
               <Button variant="ghost" className="!border-salt-teal !text-salt-teal hover:!bg-salt-teal hover:!text-white flex items-center justify-center gap-2">
                 <Globe size={18} /> Explore Florida for Good
               </Button>
               <Button variant="ghost" className="!border-white !text-white hover:!bg-white hover:!text-salt-slate flex items-center justify-center gap-2">
                 <Linkedin size={18} /> Connect on LinkedIn
               </Button>
             </div>

             <div className="flex flex-col items-center gap-4 text-xs text-gray-500 uppercase tracking-widest">
               <div className="flex items-center gap-2">
                 <Leaf size={14} className="text-salt-teal" />
                 <span>© Salt Palm Development</span>
               </div>
               <span>A Certified B Corporation & Benefit Corporation</span>
             </div>
           </div>
        </section>

      </MacOSWindow>
      
      <Dock activeSection={activeSection} onNavigate={scrollToSection} />
    </div>
  );
}